import { useEffect, useState } from 'react';
import { api, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { comboLabel, useRefData, type Combination } from '../../lib/refdata';
import type { Position, Voucher } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

interface Row {
  combo: Combination;
  position: Position | null;
}

export function MinistryDashboard() {
  const { persona } = useAuth();
  const { fy, voteByCode, loading: refLoading } = useRefData();
  const vote = voteByCode(persona?.voteCode);

  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!vote || !fy) return;
    let alive = true;
    (async () => {
      try {
        const combos = await api.get<{ items: Combination[] }>(`/api/v1/coa/combinations?voteId=${vote.id}&limit=100`);
        const positions = await Promise.all(
          combos.items.map((c) =>
            api.get<Position>(`/api/v1/warrants/position/${c.id}?fiscalYearId=${fy.id}`).catch(() => null),
          ),
        );
        if (alive) setRows(combos.items.map((combo, i) => ({ combo, position: positions[i] ?? null })));
      } catch {
        if (alive) setError('Could not load budget position');
      }
    })();
    return () => {
      alive = false;
    };
  }, [vote?.id, fy?.id]);

  const vouchersQ = useApiQuery<{ items: Voucher[] }>(
    vote ? `/api/v1/expenditure/vouchers?voteId=${vote.id}&limit=100` : null,
  );

  if (refLoading) return <Spinner label="Loading reference data…" />;
  if (!vote) return <ErrorNote message={`Vote ${persona?.voteCode ?? '?'} not found — has the chart of accounts been seeded?`} />;
  if (!fy) return <ErrorNote message="No OPEN fiscal year. Ask the Budget Division to open one." />;

  const funded = rows?.filter((r) => r.position && r.position.appropriated > 0) ?? [];
  const total = (fn: (p: Position) => number) => funded.reduce((s, r) => s + fn(r.position!), 0);
  const paid = (vouchersQ.data?.items ?? []).filter((v) => v.status === 'PAID');
  const paidTotal = paid.reduce((s, v) => s + Number.parseFloat(v.amount), 0);
  const recent = (vouchersQ.data?.items ?? []).slice(0, 6);

  return (
    <div>
      <PageHeader
        title={`${vote.code} — ${vote.name}`}
        subtitle={`Budget position · Fiscal Year ${fy.year}`}
      />

      {error ? <ErrorNote message={error} /> : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label="Appropriated" value={scr(total((p) => p.appropriated))} hint="Enacted budget allocation" />
        <StatCard label="Allotted" value={scr(total((p) => p.allotted))} hint="Spending authority released" />
        <StatCard label="Committed" value={scr(total((p) => p.committed))} hint="Reserved for open commitments" tone="warn" />
        <StatCard label="Available to commit" value={scr(total((p) => p.availableToCommit))} tone="good" hint="Allotted − committed" />
        <StatCard label="Paid this FY" value={scr(paidTotal)} hint={`${paid.length} voucher(s) settled`} />
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Budget lines</h2>
        <div className="card overflow-x-auto">
          {rows === null ? (
            <Spinner />
          ) : funded.length === 0 ? (
            <EmptyState
              title="No funded budget lines yet"
              hint="The Ministry of Finance enters appropriations and issues warrants before spending can start."
            />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Budget line</th>
                  <th className="table-head text-right">Appropriated</th>
                  <th className="table-head text-right">Allotted</th>
                  <th className="table-head text-right">Committed</th>
                  <th className="table-head text-right">Available</th>
                  <th className="table-head w-40">Utilisation</th>
                </tr>
              </thead>
              <tbody>
                {funded.map(({ combo, position: p }) => {
                  const used = p!.allotted > 0 ? Math.min(100, Math.round((p!.committed / p!.allotted) * 100)) : 0;
                  return (
                    <tr key={combo.id} className="hover:bg-slate-50">
                      <td className="table-cell">
                        <div className="font-medium">{combo.econItem?.name}</div>
                        <div className="text-xs text-slate-500">{comboLabel(combo)}</div>
                      </td>
                      <td className="table-cell text-right tabular-nums">{scr(p!.appropriated)}</td>
                      <td className="table-cell text-right tabular-nums">{scr(p!.allotted)}</td>
                      <td className="table-cell text-right tabular-nums">{scr(p!.committed)}</td>
                      <td className="table-cell text-right font-semibold tabular-nums text-seagreen-600">
                        {scr(p!.availableToCommit)}
                      </td>
                      <td className="table-cell">
                        <div className="h-2 w-full rounded-full bg-slate-100">
                          <div
                            className={`h-2 rounded-full ${used > 85 ? 'bg-rose-500' : used > 60 ? 'bg-amber-500' : 'bg-brand-500'}`}
                            style={{ width: `${used}%` }}
                          />
                        </div>
                        <div className="mt-0.5 text-[11px] text-slate-400">{used}% of allotment committed</div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-bold text-slate-700">Recent payment vouchers</h2>
        <div className="card overflow-x-auto">
          {vouchersQ.loading ? (
            <Spinner />
          ) : recent.length === 0 ? (
            <EmptyState title="No vouchers yet" hint="Capture a supplier invoice to start the payment cycle." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Voucher</th>
                  <th className="table-head text-right">Amount</th>
                  <th className="table-head">Status</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((v) => (
                  <tr key={v.id} className="hover:bg-slate-50">
                    <td className="table-cell font-medium">{v.number}</td>
                    <td className="table-cell text-right tabular-nums">{scr(v.amount)}</td>
                    <td className="table-cell">
                      <StatusBadge status={v.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
